import { action, reaction } from "mobx";
import { formReportApi } from "./api";
import { FormatDateUtc } from "helpers/FormatDateTable";
import { SIGNATURE_NOTE_PLACEHOLDER } from "views/Reports/shared/SignatureAnswerRenderer";

const formReportActions = {
  reaction: (expression, effect) => reaction(expression, effect),
  formCompletionMetrics: [],
  filteredFormCompletionMetrics: [],
  filterModel: {},
  formResponseData: null,
  formCompletionResponses: [],
  formCompletionResponsesHeader: [],
  isResponseModalOpen: false,

  getFormCompletionMetrics: action(async function() {
    try {
      this.setIsLoading(true);
      this.setHasError(false);
      this.setErrorMessage("");

      const response = await formReportApi.getFormCompletionMetrics();
      if (response.success) {
        const data = response.data.result || [];
        this.formCompletionMetrics = data;
        this.filteredFormCompletionMetrics = data;
        return data;
      } else {
        this.setHasError(true);
        this.setErrorMessage(response.data.message);
        this.formCompletionMetrics = [];
        this.filteredFormCompletionMetrics = [];
        return [];
      }
    } catch (error) {
      this.setHasError(true);
      this.setErrorMessage(
        error.message || "Failed to fetch form completion metrics"
      );
      this.formCompletionMetrics = [];
      this.filteredFormCompletionMetrics = [];
      return [];
    } finally {
      this.setIsLoading(false);
    }
  }),

  resetFilters: action(function() {
    // Reset filter model
    this.filterModel = {};

    // Create a new array with all original data
    const newData = this.formCompletionMetrics.map(item => ({ ...item }));
    this.filteredFormCompletionMetrics = newData;
  }),

  applyFilters: action(function() {
    const metrics = this.formCompletionMetrics;
    const filterModel = this.filterModel;

    let filtered = [...metrics];

    if (filterModel.state) {
      const statusValues = filterModel.state.values;
      filtered = filtered.filter(item =>
        statusValues.includes(item.state.toLowerCase())
      );
    }

    if (filterModel.category) {
      const categoryValues = filterModel.category.values;
      filtered = filtered.filter(item => {
        try {
          const categories = JSON.parse(item.category);
          return categories.some(cat => categoryValues.includes(cat));
        } catch {
          return false;
        }
      });
    }

    if (filterModel.createdDate) {
      const { dateFrom, type } = filterModel.createdDate;
      const filterDate = new Date(dateFrom);
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.createdDate);
        switch (type) {
          case "lessThan":
            return itemDate < filterDate;
          case "equals":
            return itemDate.toDateString() === filterDate.toDateString();
          case "greaterThan":
            return itemDate > filterDate;
          default:
            return true;
        }
      });
    }

    this.filteredFormCompletionMetrics = filtered;
  }),

  getFormTotalCompletion: action(async function(formId) {
    try {
      const response = await formReportApi.getFormTotalCompletion(formId);
      if (response.success) {
        this.formTotalCompletion = response.data.result;
        return response.data.result;
      } else {
        console.error(
          "Error fetching form total completion:",
          response.data.message
        );
        return null;
      }
    } catch (error) {
      console.error("Error fetching form total completion:", error);
      return null;
    }
  }),

  getFormCompletionResponses: action(async function(formId) {
    try {
      const response = await formReportApi.getFormCompletionResponses(formId);

      if (!response.success) {
        console.error(
          "Error fetching form completion responses:",
          response.data.message
        );
        return null;
      }
      
      const responses = response.data.result || [];

      // Handle empty data
      if (responses.length === 0) {
        return { columnDefs: [], rowData: [] };
      }

      // Step 1: Extract all unique questions
      const questions = this.extractQuestionsFromResponses(responses);
      const totalQuestions = questions.length;

      // Step 2: Build CSV header
      const columnDefs = this.buildResponsesColumnDefs(questions);
      
      // Step 3: Transform responses into CSV rows
      const rowData = responses.map(item =>
        this.transformResponsesToCsvRows(item, totalQuestions)
      );
      
      // Step 4: Store CSV data
      this.formCompletionResponsesHeader = columnDefs;
      this.formCompletionResponses = rowData;

      return { columnDefs, rowData };
    } catch (error) {
      console.error("Error fetching form completion responses:", error);
      return null;
    }
  }),

  getFormCompletionDetail: action(async function(formId) {
    try {
      const response = await formReportApi.getFormCompletionDetail(formId);
      if (response.success) {
        return response.data.result;
      } else {
        console.error("Error fetching form completion detail:", response.data.message);
        return null;
      }
    } catch (error) {
      console.error("Error fetching form completion detail:", error);
      return null;
    }
  }),

  getFromCompletionResponses: action(async function(id) {
    try {
      const response = await formReportApi.getFromCompletionResponses(id);
      if (response.success) {
        this.formResponseData = response.data.result;
        this.isResponseModalOpen = true;
        return response.data.result;
      }
      return null;
    } catch (error) {
      console.error("Error fetching form completion responses:", error);
      return null;
    }
  }),

  openResponseModal: action(function(data) {
    this.formResponseData = data;
    this.isResponseModalOpen = true;
  }),

  closeResponseModal: action(function() {
    this.isResponseModalOpen = false;
    this.formResponseData = null;
  }),

  // Helper method
  // Extract all unique question from completion responses
  extractQuestionsFromResponses: function(responses) {
    let maxQuestionsCount = 0;
    let longestResponses = [];

    responses.forEach(item => {
      if (item.responses && item.responses.length > maxQuestionsCount) {
        maxQuestionsCount = item.responses.length;
        longestResponses = item.responses;
      }
    });

    // Extract questions from their index position
    return longestResponses.map((response, index) => ({
      position: index,
      question: response.formQuestion || ""
    }));
  },

  // Build AgGrid column definitions for responses export
  buildResponsesColumnDefs: function(questions) {
    // Default headers
    const defaultColumns = [
      { headerName: "Contact ID", field: "contactId", minWidth: 100 },
      { headerName: "Contact Name", field: "contactName", minWidth: 200 },
      { headerName: "Email", field: "email", minWidth: 200 },
      { headerName: "Mobile", field: "mobile", minWidth: 150 },
      { headerName: "Completion Date", field: "completionDate", minWidth: 180 }
    ];

    // Add dynamic question headers
    const questionColumns = questions.map((q, index) => ({
      field: `answer-${index}`,
      headerName: q.question,
      minWidth: 150
    }));

    return [...defaultColumns, ...questionColumns];
  },

  // Transform nested response data into flat CSV rows
  transformResponsesToCsvRows: function(item, totalQuestions) {
    const {
      id,
      contactId,
      contactName = "",
      email = "",
      mobile = "",
      completionDate = "",
      responses = []
    } = item;

    // Start with default fields
    const rowData = {
      contactId,
      contactName,
      email: email || "",
      mobile: mobile || "",
      completionDate: completionDate ? FormatDateUtc(completionDate) : ""
    };

    // Map answer by position - preserve order and empty answers
    for (let i = 0; i < totalQuestions; i++) {
      const response = responses[i];
      const answer = response?.formAnswer || "";
      rowData[`answer-${i}`] = answer.startsWith("data:image") ? SIGNATURE_NOTE_PLACEHOLDER : answer;
    }

    return rowData;
  },
};

export default formReportActions;